import { useId } from 'react';
import type {
  WebsiteInterfaceLocale,
  WebsiteRegionFieldState,
  WebsiteSlotSchema,
} from '@dukanti/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { useWebsiteT } from '../../hooks/use-website-t';
import { slotFieldLabel } from '../../lib/region-labels';
import {
  authoredString,
  codePointLength,
  defaultTextOf,
  hasTemplateDefault,
} from '../../lib/slot-values';

export interface SlotFieldProps {
  readonly slot: WebsiteSlotSchema;
  readonly state: WebsiteRegionFieldState;
  readonly onChange: (state: WebsiteRegionFieldState) => void;
  /** Admin locale key of the message under the field. */
  readonly issueKey?: string;
  /** The language the store's default texts are shown in. */
  readonly interfaceLocale: WebsiteInterfaceLocale;
}

/** What the box of a text field shows for each state. */
function shownText(
  slot: WebsiteSlotSchema,
  state: WebsiteRegionFieldState,
  locale: WebsiteInterfaceLocale,
): string {
  if (state.mode === 'authored') return authoredString(state) ?? '';
  if (state.mode === 'default') return defaultTextOf(slot, locale) ?? '';
  return '';
}

/**
 * One text field of a region: the template's own words until the merchant
 * writes theirs, a counter in code points against the schema's limit, "Use
 * template text" where the template has words, and "Leave empty" where the
 * slot is optional. Text is kept exactly as typed; the server checks it again.
 */
export function SlotTextField({
  slot,
  state,
  onChange,
  issueKey,
  interfaceLocale,
}: SlotFieldProps): React.JSX.Element {
  const { t } = useWebsiteT();
  const id = useId();
  const messageId = `${id}-message`;
  const text = shownText(slot, state, interfaceLocale);
  const count = codePointLength(text);
  const max = slot.maxLength;
  const tooLong = max !== undefined && count > max;

  const commonProps = {
    id,
    value: text,
    // The store's texts are written in whatever language the merchant writes.
    dir: 'auto' as const,
    'aria-invalid': issueKey !== undefined || tooLong ? true : undefined,
    'aria-describedby': issueKey === undefined ? undefined : messageId,
    onChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = event.target.value;
      // Clearing an optional field means "leave it empty", not an empty text.
      if (next === '' && !slot.required) onChange({ mode: 'empty' });
      else onChange({ mode: 'authored', value: { v: next } });
    },
  };

  return (
    <div className="space-y-1.5" data-testid={`field-${slot.id}`}>
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor={id}>{slotFieldLabel(t, slot)}</Label>
        {max === undefined ? null : (
          <span
            aria-live="polite"
            className={cn('text-xs', tooLong ? 'text-destructive' : 'text-muted-foreground')}
          >
            {t('admin.websiteTemplates.dialog.charCount', { count, max })}
          </span>
        )}
      </div>
      {slot.multiline ? <Textarea rows={4} {...commonProps} /> : <Input {...commonProps} />}
      {slot.requiresMerchantValue && state.mode !== 'authored' ? (
        <p className="text-muted-foreground text-xs">
          {t('admin.websiteTemplates.dialog.required')}
        </p>
      ) : null}
      {issueKey === undefined ? null : (
        <p id={messageId} role="alert" className="text-destructive text-xs">
          {t(issueKey)}
        </p>
      )}
      {hasTemplateDefault(slot) || !slot.required ? (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {hasTemplateDefault(slot) && state.mode !== 'default' ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => onChange({ mode: 'default' })}
            >
              {t('admin.websiteTemplates.dialog.useDefault')}
            </Button>
          ) : null}
          {!slot.required && state.mode !== 'empty' ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0"
              onClick={() => onChange({ mode: 'empty' })}
            >
              {t('admin.websiteTemplates.dialog.leaveEmpty')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
